import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import {
  getCreateFromDocumentUploadUrl,
  pollCreateFromDocument,
  startCreateFromDocument,
} from '~/api/createFromDocument';

const POLL_INTERVAL_MS = 5000;

export type CreateFromDocumentStatus = 'uploading' | 'processing' | 'complete' | 'error';

export type CreateFromDocumentResult =
  | { type: 'project'; projectId: string; projectName?: string }
  | { type: 'portfolio'; portfolioId: string; portfolioName?: string; projectIds: string[] }
  | { type: 'error'; message?: string; details?: string; step?: string };

export interface CreateFromDocumentRequest {
  installerId: string;
  fileName: string;
  additionalContext?: string;
}

interface CreateFromDocumentProgressState {
  apiEventId: string | null;
  status: CreateFromDocumentStatus | null;
  statusMessage: string | null;
  result: CreateFromDocumentResult | null;
  request: CreateFromDocumentRequest | null;
}

export interface StartIngestionParams {
  file: File;
  installerId: string;
  additionalContext?: string;
}

interface CreateFromDocumentProgressContextValue extends CreateFromDocumentProgressState {
  startIngestion: (params: StartIngestionParams) => Promise<void>;
  rerun: () => Promise<void>;
  canRerun: boolean;
  setComplete: (result: CreateFromDocumentResult) => void;
  setError: (result?: CreateFromDocumentResult) => void;
  dismiss: () => void;
}

const initialState: CreateFromDocumentProgressState = {
  apiEventId: null,
  status: null,
  statusMessage: null,
  result: null,
  request: null,
};

const CreateFromDocumentProgressContext =
  createContext<CreateFromDocumentProgressContextValue | null>(null);

export function CreateFromDocumentProgressProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CreateFromDocumentProgressState>(initialState);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileRef = useRef<File | null>(null);

  const setComplete = useCallback((result: CreateFromDocumentResult) => {
    setState((prev) => ({
      ...prev,
      status: 'complete',
      result,
    }));
  }, []);

  const setError = useCallback((result?: CreateFromDocumentResult) => {
    setState((prev) => ({
      ...prev,
      status: 'error',
      result: result ?? { type: 'error', message: 'Import failed.' },
    }));
  }, []);

  const dismiss = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    fileRef.current = null;
    setState(initialState);
  }, []);

  const startIngestion = useCallback(async ({ file, installerId, additionalContext }: StartIngestionParams) => {
    fileRef.current = file;
    const request: CreateFromDocumentRequest = {
      installerId,
      fileName: file.name,
      additionalContext: additionalContext?.trim() || undefined,
    };
    setState({
      apiEventId: null,
      status: 'uploading',
      statusMessage: 'Uploading document…',
      result: null,
      request,
    });
    try {
      const { presignedUrl, s3Key } = await getCreateFromDocumentUploadUrl(
        installerId,
        file.name,
        file.size
      );
      await axios.put(presignedUrl, file);
      const idempotencyKey =
        crypto.randomUUID?.() ??
        `create-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const { apiEvent } = await startCreateFromDocument({
        s3Key,
        fileName: file.name,
        installerId,
        idempotencyKey,
        additionalContext: request.additionalContext,
      });
      setState((prev) => ({
        ...prev,
        apiEventId: apiEvent.id,
        status: 'processing',
        statusMessage: 'Starting…',
        result: null,
      }));
    } catch (err: any) {
      const message =
        err?.response?.data?.message ?? err?.message ?? 'Upload or start failed.';
      setState((prev) => ({
        ...prev,
        status: 'error',
        result: { type: 'error', message },
      }));
      throw err;
    }
  }, []);

  const rerun = useCallback(async () => {
    const file = fileRef.current;
    const request = state.request;
    if (!file || !request) return;
    await startIngestion({
      file,
      installerId: request.installerId,
      additionalContext: request.additionalContext,
    });
  }, [state.request, startIngestion]);

  useEffect(() => {
    if (state.status !== 'processing' || !state.apiEventId) return;

    const poll = async () => {
      try {
        const response = await pollCreateFromDocument(state.apiEventId!);
        if (response.statusMessage) {
          setState((prev) => ({ ...prev, statusMessage: response.statusMessage! }));
        }
        if (response.status === 'SUCCESS' && response.result) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setComplete(response.result as CreateFromDocumentResult);
          return;
        }
        if (response.status === 'ERROR') {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          const errResult = response.result as { message?: string; details?: string; step?: string } | undefined;
          setError(
            errResult
              ? { type: 'error', message: errResult.message ?? 'Import failed.', details: errResult.details, step: errResult.step }
              : { type: 'error', message: 'Import failed.' }
          );
          return;
        }
      } catch (err: any) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        const errorMessage =
          err?.response?.data?.errorMessage ?? err?.response?.data?.message;
        setError({ type: 'error', message: errorMessage ?? 'Import failed.' });
      }
    };

    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
    poll();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [state.apiEventId, state.status, setComplete, setError]);

  const value: CreateFromDocumentProgressContextValue = {
    ...state,
    startIngestion,
    rerun,
    canRerun: state.request != null,
    setComplete,
    setError,
    dismiss,
  };

  return (
    <CreateFromDocumentProgressContext.Provider value={value}>
      {children}
    </CreateFromDocumentProgressContext.Provider>
  );
}

export function useCreateFromDocumentProgress() {
  const ctx = useContext(CreateFromDocumentProgressContext);
  if (!ctx) {
    return null;
  }
  return ctx;
}
